import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { getCurrentMillis } from '../../utils/dateUtils';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { User as UserIcon, Camera, Save, Edit3, X } from 'lucide-react';
import type { User } from '../../types/user';
import { SectionHeader } from '../../components/ui';

const Profile: React.FC = () => {
  const { user, setUser } = useAuth();
  const { strings, language, isRTL } = useLocalization();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    phoneNumber2: '',
    nationalID: '',
    birthdate: '',
    gender: '',
    address: '',
    photoURL: ''
  });

  // Load user data
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        middleName: user.middleName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        phoneNumber2: user.phoneNumber2 || '',
        nationalID: user.nationalID || '',
        birthdate: user.birthdate || '',
        gender: user.gender || '',
        address: user.address || '',
        photoURL: user.photoURL || ''
      });
    }
  }, [user]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle photo upload
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        setFormData(prev => ({
          ...prev,
          photoURL: base64String
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Convert Base64 to data URL for display
  const getPhotoDataUrl = (base64String: string) => {
    if (!base64String) return '';
    // If it's already a data URL, return as is
    if (base64String.startsWith('data:')) return base64String;
    // If it's just base64, add the data URL prefix
    return `data:image/jpeg;base64,${base64String}`;
  };

  // Save profile changes
  const handleSave = async () => {
    if (!user?.id) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const userRef = doc(db, 'Users', user.id);
      await updateDoc(userRef, {
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        phoneNumber2: formData.phoneNumber2,
        nationalID: formData.nationalID,
        birthdate: formData.birthdate,
        gender: formData.gender,
        address: formData.address,
        photoURL: formData.photoURL,
        updatedAt: getCurrentMillis()
      });

      // Update local user state
      setUser((prev: User | null) => prev ? {
        ...prev,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        phoneNumber2: formData.phoneNumber2,
        nationalID: formData.nationalID,
        birthdate: formData.birthdate,
        gender: formData.gender,
        address: formData.address,
        photoURL: formData.photoURL
      } : null);

        setSuccess(strings.profile.profileUpdated);
        setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(strings.profile.updateError);
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        middleName: user.middleName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        phoneNumber2: user.phoneNumber2 || '',
        nationalID: user.nationalID || '',
        birthdate: user.birthdate || '',
        gender: user.gender || '',
        address: user.address || '',
        photoURL: user.photoURL || ''
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleRefresh = () => {
    // TODO: Add refresh functionality
    console.log('Refresh profile clicked');
  };

  const handleAddProfile = () => {
    setIsEditing(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">Loading profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={strings.profile.title}
          description={strings.profile.personalInfo}
          language={language}
          isRTL={isRTL}
          refreshButton={{
            onClick: handleRefresh,
            disabled: false,
            loading: false
          }}
          addButton={{
            label: language === 'ar' ? 'تعديل الملف الشخصي' : 'Edit Profile',
            onClick: handleAddProfile
          }}
        />

        {/* Save/Cancel Buttons - Only show when editing */}
        {isEditing && (
          <div className={`flex items-center space-x-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={handleCancel}
              className="px-4 py-3 bg-slate-600 text-white rounded-2xl hover:bg-slate-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="group relative flex items-center space-x-2 px-4 py-3 bg-primary text-white rounded-2xl hover:bg-blue-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg hover:shadow-xl"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span className="font-semibold">{saving ? strings.common.loading : strings.profile.saveChanges}</span>
            </button>
          </div>
        )}

        <div className="space-y-6">
          {/* Success/Error Messages */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <p className="text-green-800 dark:text-green-200 text-sm">{success}</p>
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Profile Content */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-8">
            {/* Photo Section */}
            <div className="flex items-center space-x-6 mb-8">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                  {formData.photoURL ? (
                    <img
                      src={getPhotoDataUrl(formData.photoURL)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-12 h-12 text-slate-400" />
                  )}
                </div>
                {isEditing && (
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors">
                    <Camera className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {formData.firstName} {formData.middleName} {formData.lastName}
                </h2>
                <p className="text-slate-600 dark:text-slate-400">{formData.email}</p>
                <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                  {user?.role || 'Lawyer'} • {user?.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.firstName}
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white"
                />
              </div>

              {/* Middle Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.middleName}
                </label>
                <input
                  type="text"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.lastName}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white"
                />
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.email}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled={true}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{strings.profile.email}</p>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.phoneNumber}
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white"
                />
              </div>

              {/* Phone Number 2 */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.phoneNumber2}
                </label>
                <input
                  type="tel"
                  name="phoneNumber2"
                  value={formData.phoneNumber2}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white"
                />
              </div>

              {/* National ID */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.nationalID}
                </label>
                <input
                  type="text"
                  name="nationalID"
                  value={formData.nationalID}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white"
                />
              </div>

              {/* Birth Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.birthdate}
                </label>
                <input
                  type="date"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.gender}
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="1"
                      checked={formData.gender === '1'}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="mr-2 text-blue-600 focus:ring-primary disabled:cursor-not-allowed"
                    />
                    <span className="text-slate-700 dark:text-slate-300">{strings.profile.male}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="gender"
                      value="2"
                      checked={formData.gender === '2'}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="mr-2 text-blue-600 focus:ring-primary disabled:cursor-not-allowed"
                    />
                    <span className="text-slate-700 dark:text-slate-300">{strings.profile.female}</span>
                  </label>
                </div>
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {strings.profile.address}
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-white resize-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
